import { useEffect, useRef } from "react";
import { BrowserRouter, HashRouter, useLocation } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import Navbar from "@/components/feature/Navbar";
import { AppRoutes } from "./router";
import Footer from "@/components/feature/Footer";
import MaintenancePage from "@/pages/Maintenance";
import { useSiteSettings } from "@/lib/siteSettings";

/**
 * Scroll to the top on every page change, or to the `#fragment` when the new
 * URL carries one (e.g. /membership#join). Same-page anchor clicks do not
 * change the pathname and are left to the browser (smooth scrolling).
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const lastPath = useRef<string | null>(null);
  useEffect(() => {
    const pathChanged = lastPath.current !== pathname;
    lastPath.current = pathname;
    if (!pathChanged) return;
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: "instant", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

// When embedded in a preview/sandbox host, the page is often served at an
// unpredictable, non-root URL path. BrowserRouter matches routes against
// that real path, so "/about" etc. would never match and every page falls
// through to the 404 route. HashRouter keeps all routing after "#", which
// works no matter what path the host serves the file from. Normal
// production builds (IS_PREVIEW unset) keep using BrowserRouter with clean
// URLs.
const Router = __IS_PREVIEW__ ? HashRouter : BrowserRouter;

function isAdminPath(pathname: string): boolean {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

// The admin backend (/admin/*) ships its own chrome (sidebar, top bar), so the
// public navigation and footer are omitted there. Public routes wait for the
// site settings and, while the site is taken down (後台 → 網站設定 → 網站上線
// 狀態), render only the maintenance page — the backend keeps working.
function Layout() {
  const { pathname } = useLocation();
  const isAdmin = isAdminPath(pathname);
  const { settings, loaded } = useSiteSettings();

  if (!isAdmin) {
    // Nothing public is painted before the settings arrive, so a site that is
    // taken down never flashes its real content.
    if (!loaded) return <div className="min-h-screen bg-navy-950" aria-busy="true" data-testid="site-loading" />;
    if (settings.maintenance.enabled) return <MaintenancePage />;
  }

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      <AppRoutes />
      {!isAdmin && <Footer />}
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <Router basename={__IS_PREVIEW__ ? undefined : __BASE_PATH__}>
        <Layout />
      </Router>
    </I18nextProvider>
  );
}

export default App;
